/**
 * AUTO-GENERATED by scripts/build-tokens.mjs — DO NOT EDIT BY HAND.
 * Source of truth: src/design/figma/*.tokens.json (Figma "Kickfin - Design System").
 * Regenerate with: yarn tokens:build
 *
 * Three tiers: primitives (raw scales) -> semantic (intent) -> components (per-component).
 * Semantic/component tokens reference their primitive, mirroring the Figma variable graph.
 * Font size/weight/line-height are generated here; font family remains in fonts.ts.
 * Line-height is raw px — convert to % per type role at the call site via lineHeight().
 */
/* eslint-disable */

export const primitives = {
  color: {
    primary: {
      '25': "#EDE8FC",
      '50': "#DCD1FA",
      '100': "#CABAF7",
      '200': "#A68CF2",
      '300': "#9575F0",
      '400': "#835EED",
      '500': "#6031E8",
      '600': "#4617CE",
      '700': "#3712A1",
      '800': "#270D73",
      '900': "#170845",
    },
    secondary: {
      '25': "#CCF9FF",
      '50': "#99F3FF",
      '100': "#66EDFF",
      '200': "#33E7FF",
      '300': "#00E0FF",
      '400': "#00B2D0",
      '500': "#0086A3",
      '600': "#005C78",
      '700': "#003550",
      '800': "#00293D",
      '900': "#001B29",
    },
    neutral: {
      '25': "#FCFCFD",
      '50': "#F9FAFB",
      '100': "#F2F4F7",
      '200': "#E4E7EC",
      '300': "#D0D5DD",
      '400': "#98A2B3",
      '500': "#667085",
      '600': "#475467",
      '700': "#344054",
      '800': "#1D2939",
      '900': "#101828",
    },
    info: {
      '25': "#D5F3FF",
      '50': "#338EC7",
      '100': "#08678D",
    },
    success: {
      '25': "#F2FAF3",
      '50': "#37C34A",
      '100': "#22802F",
    },
    warning: {
      '25': "#FFFADD",
      '50': "#F7DD47",
      '100': "#6E5200",
    },
    danger: {
      '25': "#FFEBEE",
      '50': "#EA3333",
      '100': "#830202",
    },
    cash: {
      '25': "#FFEAF4",
      '50': "#C44BA1",
      '100': "#8F2E71",
    },
    white: {
      '25': "#FFFFFF",
    },
  },
  fontSize: {
    xxs: 11,
    xs: 12,
    sm: 13,
    md: 14,
    lg: 16,
    xl: 18,
    '2xl': 20,
    '3xl': 24,
    '4xl': 28,
    '5xl': 32,
    '6xl': 40,
  },
  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
  lineHeight: {
    xxs: 16,
    xs: 18,
    sm: 20,
    md: 24,
    lg: 26,
    xl: 28,
    xl1: 32,
    xl2: 36,
    xl3: 40,
    xl4: 48,
  },
  space: {
    '1': 2,
    '2': 4,
    '3': 8,
    '4': 12,
    '5': 16,
    '6': 24,
    '7': 32,
    '8': 48,
    '9': 64,
    '10': 96,
  },
  radius: {
    none: 0,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 22,
    full: 9999,
  },
  iconSize: {
    xs: 8,
    sm: 12,
    md: 16,
    lg: 24,
    xl: 32,
  },
  avatarSize: {
    xsmall: 24,
    small: 32,
    medium: 40,
    large: 56,
    xlarge: 80,
  },
  shadowColor: {
    'color-1': "#101828",
    'color-2': "#101828",
    'color-3': "#101828",
    'color-4': "#101828",
    'color-5': "#101828",
    'color-6': "#101828",
    'offset-y-1': 1,
    'offset-y-2': 2,
    'offset-y-3': 4,
    'offset-y-4': 8,
    'offset-y-5': 12,
    'offset-y-6': 24,
    'blur-1': 2,
    'blur-2': 3,
    'blur-3': 4,
    'blur-4': 8,
    'blur-5': 16,
    'blur-6': 24,
    'blur-7': 48,
  },
  input: {
    height: 44,
  },
  tooltip: {
    'arrow-size': 8,
    'max-width': 240,
  },
} as const;

export const semantic = {
  bg: {
    canvas: primitives.color.neutral['25'],
    surface: primitives.color.white['25'],
    subtle: primitives.color.neutral['50'],
    muted: primitives.color.neutral['100'],
    neutral: primitives.color.neutral['200'],
    inverse: primitives.color.neutral['900'],
    brand: primitives.color.primary['500'],
    'brand-light': primitives.color.primary['25'],
    'brand-strong': primitives.color.primary['700'],
    secondary: primitives.color.secondary['400'],
    'secondary-light': primitives.color.secondary['300'],
    'secondary-strong': primitives.color.secondary['500'],
    'brand-subtle': primitives.color.primary['25'],
    cash: primitives.color.cash['50'],
    'cash-subtle': primitives.color.cash['25'],
    'inverse-soft': primitives.color.neutral['800'],
    'inverse-light': primitives.color.neutral['600'],
    'secondary-subtle': primitives.color.secondary['50'],
    'secondary-medium': primitives.color.secondary['500'],
    'success-subtle': primitives.color.success['25'],
    success: primitives.color.success['50'],
    'warning-subtle': primitives.color.warning['25'],
    warning: primitives.color.warning['50'],
    'danger-subtle': primitives.color.danger['25'],
    danger: primitives.color.danger['50'],
    'info-subtle': primitives.color.info['25'],
    disable: primitives.color.neutral['300'],
  },
  text: {
    primary: primitives.color.neutral['900'],
    secondary: primitives.color.neutral['800'],
    tertiary: primitives.color.neutral['600'],
    disabled: primitives.color.neutral['500'],
    light: primitives.color.neutral['300'],
    inverse: primitives.color.white['25'],
    brand: primitives.color.primary['600'],
    'on-brand': primitives.color.white['25'],
    cash: primitives.color.cash['100'],
    link: primitives.color.primary['500'],
    'link-hover': primitives.color.primary['600'],
    danger: primitives.color.danger['50'],
    'secondary-medium': primitives.color.secondary['400'],
    success: primitives.color.success['100'],
    warning: primitives.color.warning['100'],
    info: primitives.color.info['100'],
  },
  border: {
    subtle: primitives.color.neutral['100'],
    default: primitives.color.neutral['200'],
    strong: primitives.color.neutral['300'],
    brand: primitives.color.primary['500'],
    secondary: primitives.color.secondary['400'],
    focus: primitives.color.neutral['500'],
    inverse: primitives.color.neutral['900'],
  },
  space: {
    '2xs': primitives.space['1'],
    xs: primitives.space['2'],
    sm: primitives.space['3'],
    md: primitives.space['4'],
    lg: primitives.space['5'],
    xl: primitives.space['6'],
    '2xl': primitives.space['7'],
    '3xl': primitives.space['8'],
    '4xl': primitives.space['9'],
    '5xl': primitives.space['10'],
  },
} as const;

export const components = {
  button: {
    radius: primitives.radius['full'],
    primary: {
      bg: {
        default: semantic['bg']['brand'],
        hover: semantic['bg']['brand-strong'],
        disabled: semantic['bg']['neutral'],
      },
      text: {
        default: semantic['text']['on-brand'],
        hover: semantic['text']['on-brand'],
        disabled: semantic['text']['disabled'],
      },
      icon: {
        default: semantic['text']['on-brand'],
        disabled: semantic['text']['disabled'],
      },
    },
    secondary: {
      bg: {
        default: primitives.color.secondary['300'],
        hover: primitives.color.secondary['200'],
        disabled: semantic['bg']['neutral'],
      },
      text: {
        default: semantic['text']['primary'],
        hover: semantic['text']['primary'],
        disabled: semantic['text']['disabled'],
      },
      icon: {
        default: semantic['text']['primary'],
        disabled: semantic['text']['disabled'],
      },
    },
    destructive: {
      bg: {
        default: primitives.color.danger['50'],
        hover: primitives.color.danger['100'],
        disabled: semantic['bg']['neutral'],
      },
      text: {
        default: semantic['text']['inverse'],
        hover: semantic['text']['inverse'],
        disabled: semantic['text']['disabled'],
      },
      icon: {
        default: semantic['text']['inverse'],
        disabled: semantic['text']['disabled'],
      },
    },
    ghost: {
      bg: {
        default: semantic['bg']['surface'],
        hover: semantic['bg']['subtle'],
        disabled: semantic['bg']['neutral'],
      },
      text: {
        default: semantic['text']['primary'],
        hover: semantic['text']['primary'],
        disabled: semantic['text']['disabled'],
      },
      border: {
        default: semantic['border']['strong'],
        hover: semantic['border']['strong'],
        disabled: semantic['border']['subtle'],
      },
      icon: {
        default: semantic['text']['primary'],
        disabled: semantic['text']['disabled'],
      },
    },
    inverse: {
      bg: {
        default: semantic['bg']['inverse'],
        hover: semantic['bg']['inverse-soft'],
        disabled: semantic['bg']['neutral'],
      },
      text: {
        default: semantic['text']['inverse'],
        hover: semantic['text']['inverse'],
        disabled: semantic['text']['disabled'],
      },
      icon: {
        default: semantic['text']['inverse'],
        disabled: semantic['text']['disabled'],
      },
    },
  },
  icon: {
    color: {
      default: semantic['text']['secondary'],
      inverse: semantic['bg']['canvas'],
      light: primitives.color.neutral['500'],
      disable: primitives.color.neutral['400'],
      danger: primitives.color.danger['50'],
      warning: primitives.color.warning['50'],
      success: primitives.color.success['50'],
      info: primitives.color.info['50'],
      Primary: primitives.color.secondary['400'],
      brand: primitives.color.primary['500'],
    },
  },
  avatar: {
    radius: primitives.radius['full'],
    initials: {
      bg: semantic['bg']['inverse'],
      text: semantic['text']['inverse'],
    },
    image: {
      placeholder: {
        bg: semantic['border']['strong'],
        icon: semantic['bg']['surface'],
      },
    },
  },
  input: {
    radius: primitives.radius['xs'],
    bg: {
      placeholder: semantic['bg']['surface'],
      focus: semantic['bg']['surface'],
      disabled: semantic['bg']['muted'],
      error: semantic['bg']['surface'],
    },
    border: {
      placeholder: semantic['border']['default'],
      focus: semantic['border']['focus'],
      disabled: semantic['border']['default'],
      error: primitives.color.danger['50'],
    },
    text: {
      placeholder: semantic['text']['tertiary'],
      filled: semantic['text']['primary'],
      focus: semantic['text']['primary'],
      disabled: semantic['text']['disabled'],
      error: semantic['text']['primary'],
    },
  },
  'text-link': {
    text: {
      default: semantic['text']['link'],
      hover: semantic['text']['link-hover'],
      disabled: semantic['text']['disabled'],
    },
    icon: {
      default: semantic['text']['link'],
      hover: semantic['text']['link-hover'],
      disabled: semantic['text']['disabled'],
    },
    gap: {
      small: semantic['space']['2xs'],
      medium: semantic['space']['xs'],
    },
  },
  field: {
    label: {
      text: semantic['text']['secondary'],
    },
    required: {
      text: primitives.color.danger['50'],
    },
    action: {
      text: semantic['text']['link'],
    },
    helper: {
      text: semantic['text']['tertiary'],
    },
  },
  select: {
    trigger: {
      radius: primitives.radius['xs'],
      bg: {
        placeholder: semantic['bg']['surface'],
        focus: semantic['bg']['surface'],
        disabled: semantic['bg']['muted'],
        error: semantic['bg']['surface'],
        open: semantic['bg']['surface'],
      },
      border: {
        placeholder: semantic['border']['default'],
        focus: semantic['border']['focus'],
        disabled: semantic['border']['default'],
        error: primitives.color.danger['50'],
        open: semantic['border']['focus'],
      },
      text: {
        placeholder: semantic['text']['tertiary'],
        focus: semantic['text']['primary'],
        disabled: semantic['text']['disabled'],
        error: semantic['text']['primary'],
        open: semantic['text']['primary'],
      },
    },
    panel: {
      bg: semantic['bg']['surface'],
      border: semantic['border']['default'],
      radius: primitives.radius['xs'],
    },
  },
  option: {
    bg: {
      hover: semantic['bg']['subtle'],
      selected: semantic['bg']['muted'],
    },
    text: {
      default: semantic['text']['primary'],
      disabled: semantic['text']['disabled'],
    },
    icon: {
      default: semantic['text']['secondary'],
      disabled: semantic['text']['disabled'],
    },
  },
  'option-group': {
    text: semantic['text']['tertiary'],
  },
  divider: {
    color: semantic['border']['subtle'],
  },
  checkbox: {
    radius: primitives.radius['xs'],
    indicator: {
      bg: {
        unchecked: semantic['bg']['surface'],
        checked: semantic['bg']['secondary'],
        disabled: semantic['bg']['muted'],
      },
      border: {
        unchecked: semantic['border']['strong'],
        checked: semantic['bg']['secondary'],
        disabled: semantic['border']['default'],
      },
      check: {
        default: semantic['text']['on-brand'],
        disabled: semantic['text']['disabled'],
      },
    },
    label: {
      default: semantic['text']['primary'],
      disabled: semantic['text']['disabled'],
    },
  },
  radio: {
    indicator: {
      bg: {
        unchecked: semantic['bg']['surface'],
        checked: semantic['bg']['surface'],
        disabled: semantic['bg']['muted'],
      },
      border: {
        unchecked: semantic['border']['strong'],
        checked: semantic['bg']['secondary'],
        disabled: semantic['border']['default'],
      },
      dot: {
        checked: semantic['bg']['secondary'],
        disabled: semantic['text']['disabled'],
      },
    },
    label: {
      default: semantic['text']['primary'],
      disabled: semantic['text']['disabled'],
    },
  },
  toggle: {
    track: {
      radius: primitives.radius['full'],
      bg: {
        off: semantic['border']['strong'],
        on: semantic['bg']['secondary'],
        disabled: semantic['bg']['neutral'],
      },
    },
    thumb: {
      bg: semantic['bg']['surface'],
      border: semantic['border']['subtle'],
      radius: primitives.radius['full'],
    },
    label: {
      default: semantic['text']['primary'],
      disabled: semantic['text']['disabled'],
    },
  },
  tooltip: {
    bg: semantic['bg']['inverse'],
    text: semantic['text']['inverse'],
    radius: primitives.radius['sm'],
    'padding-x': semantic['space']['md'],
    'padding-y': semantic['space']['sm'],
    'font-size': primitives.fontSize['xs'],
  },
} as const;

export type Primitives = typeof primitives;
export type Semantic = typeof semantic;
export type Components = typeof components;

export const tokens = { primitives, semantic, components } as const;
export type Tokens = typeof tokens;
